import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';

import { mainLogger } from '../logger';

const logger = mainLogger.createChild('useSessionStorage');

export function useSessionStorage<T>(key: string, initialValue: T) {
  const [storedValue, setStoredValue] = useState<T>(() => {
    /* istanbul ignore if */
    if (typeof window === 'undefined') {
      return initialValue;
    }

    try {
      const item = window.sessionStorage.getItem(key);

      return item ? (JSON.parse(item) as T) : initialValue;
    } catch (error) {
      logger.error(new Error('Invalid json in session storage', { cause: error }));

      return initialValue;
    }
  });

  const setValue: Dispatch<SetStateAction<T>> = (valueOrFunction) => {
    setStoredValue((prevValue) => {
      try {
        const valueToStore =
          typeof valueOrFunction === 'function' ? (valueOrFunction as (prevState: T) => T)(prevValue) : valueOrFunction;

        if (typeof window !== 'undefined') {
          window.sessionStorage.setItem(key, JSON.stringify(valueToStore));
        }

        return valueToStore;
      } catch (error) {
        logger.error(new Error('Cannot set sessoin storage value', { cause: error }));

        return prevValue;
      }
    });
  };

  return [storedValue, setValue] as const;
}
